import { CommonModule } from '@angular/common';
import { NgModule } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgxSummernoteModule } from 'ngx-summernote';

import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DialogModule } from 'primeng/dialog';
import { OrderListModule } from 'primeng/orderlist';
import { PanelModule } from 'primeng/panel';

import { HisBreadcrumbComponent } from './component/header/his-breadcrumb.component';
import { HisHeaderComponent } from './component/header/his-header.component';
import { HisPageOptionPanelComponent } from './component/header/his-page-option-panel.component';
import { HisHttplistenerComponent } from './component/loader/his-httplistener.component';
import { HisPageComponent } from './component/page/his-page.component';

import { HisSummernoteComponent } from './component/summernote/his-summernote.component';
import { HisAutoCompleteEagerDataSourceDirective } from './directive/autocomplete/his-autocomplete-eagerdatasource.directive';
import { HisAutoCompleteLazyDataSourceDirective } from './directive/autocomplete/his-autocomplete-lazydatasource.directive';
import { HisButtonTitleDirective } from './directive/his-buttonTitle.directive';
import { HisCalendarDefaultsDirective } from './directive/his-calendar.directive';
import { HisChipsDirective } from './directive/his-chips.directive';
import { HisDdDirective } from './directive/his-dd.directive';
import { HisDeleteActionDirective } from './directive/his-deleteAction.directive';
import { HisFileUploadDirective } from './directive/his-file-upload.directive';
import { HisFormDefaultsDirective } from './directive/his-form.directive';
import { HisFrozenColDirective } from './directive/his-frozenCol.directive';
import { HisImageDirective } from './directive/his-image.directive';
import { HisListboxDirective } from './directive/his-listbox.directive';
import { HisModificationDetectionDirective } from './directive/his-modification-detection.directive';
import { HisMultiselectDefaultsDirective } from './directive/his-multiselect.directive';
import { HisPageTitleDirective } from './directive/his-page-title.directive';
import { HisPanelHelptextDirective } from './directive/his-panel-helptext.directive';
import { HisPanelDirective } from './directive/his-panel.directive';
import { HisPlamDirective } from './directive/his-plam.directive';
import { HisRestoreFocusDirective } from './directive/his-restore-focus.directive';
import { HisScrollwithmeDirective } from './directive/his-scrollwithme.directive';
import { HisStepsDirective } from './directive/his-steps.directive';
import { HisTableDirective } from './directive/his-table.directive';

import { HisSafeHtmlComponent } from '../display/component/his-display-safe-html.component';


import { HisDdValidator } from './validator/his-dd-validator';

import { HisPlamComponent } from '../structure/component/plam/his-plam.component';
import { HisDdPopupComponent } from '../structure/component/dd-popup/his-dd-popup.component';
import { HisConfirmDialogComponent } from './component/confirmdialog/his-confirm-dialog.component';
import { NotificationComponent } from './component/notifications/notification.component';
import { NotificationsComponent } from './component/notifications/notifications.component';
import { HisHtmlSafePipe } from './pipe/his-html-safe.pipe';
import { HisHtmlUnsafePipe } from './pipe/his-html-unsafe.pipe';
import { HisReplacePipe } from './pipe/his-replace.pipe';
import { HisUiPipe } from './pipe/his-ui.pipe';
import { HisUiddPipe } from './pipe/his-uidd.pipe';

import { HisBrowserBackButtonLockComponent } from './component/browser-back-button-lock/his-browser-back-button-lock.component';
import { HisRowsPerPageComponent } from './component/rows-per-page/his-rows-per-page.component';
import { HisWaitingAnimationComponent } from './component/waiting-animation/his-waiting-animation.component';
import { HisDialogDirective } from './directive/his-dialog.directive';
import { HisGlobalRestoreFocusDirective } from './directive/his-global-restore-focus.directive';
import { HisIntegerValidatorDirective } from './directive/his-integer.directive';
import { HisNumberValidatorDirective } from './directive/his-number.directive';
import { HisScrollNearEndDirective } from './directive/his-scrollnearend.directive';
import { HisSearchResultHeaderContextProviderDirective } from './directive/his-search-result-header-context-provider.directive';
import { HisTrimInputDirective } from './directive/his-triminput.directive';
import { HisAmountPipe } from './pipe/his-amount.pipe';
import { HisDatePipe } from './pipe/his-date.pipe';
import { HisLowercasefirstletterPipe } from './pipe/his-lowercasefirstletter.pipe';
import { HisUppercasefirstletterPipe } from './pipe/his-uppercasefirstletter.pipe';
import { HisDdContextsDirective, HisViewContextDirective } from './directive/his-dd-contexts.directive';

/**
 * SharedModule with very basic components, directives, and pipes to import in child
 * modules of HIS Angular applications. Import HisCoreModule in the root module.
 */
@NgModule( {
    imports: [
        CommonModule,
        FormsModule,       // required for HisFooterComponent
        BreadcrumbModule,
        ConfirmDialogModule,
        DialogModule,
        PanelModule,
        OrderListModule,
        NgxSummernoteModule,
        HisSafeHtmlComponent,
        HisDdPopupComponent,
        HisViewContextDirective,
        HisDdContextsDirective,
    ],

    declarations: [
        // Components
        HisHeaderComponent,
        HisPageComponent,
        HisPageOptionPanelComponent,
        HisBreadcrumbComponent,
        NotificationComponent,
        NotificationsComponent,
        HisHttplistenerComponent,
        HisConfirmDialogComponent,
        HisBrowserBackButtonLockComponent,
        HisPlamComponent,
        HisRowsPerPageComponent,


        // Directives
        HisImageDirective,
        HisDdDirective,
        HisPlamDirective,
        HisDdValidator,
        HisAutoCompleteEagerDataSourceDirective,
        HisAutoCompleteLazyDataSourceDirective,
        HisCalendarDefaultsDirective,
        HisChipsDirective,
        HisFormDefaultsDirective,
        HisPageTitleDirective,
        HisModificationDetectionDirective,
        HisMultiselectDefaultsDirective,
        HisRestoreFocusDirective,
        HisScrollwithmeDirective,
        HisFrozenColDirective,
        HisButtonTitleDirective,
        HisDeleteActionDirective,
        HisDialogDirective,
        HisGlobalRestoreFocusDirective,
        HisStepsDirective,
        HisScrollNearEndDirective,
        HisSummernoteComponent,
        HisTrimInputDirective,
        HisFileUploadDirective,
        HisPanelDirective,
        HisPanelHelptextDirective,
        HisTableDirective,
        HisSearchResultHeaderContextProviderDirective,
        HisIntegerValidatorDirective,
        HisNumberValidatorDirective,
        HisWaitingAnimationComponent,
        HisListboxDirective,


        // Pipes
        HisAmountPipe,
        HisHtmlUnsafePipe,
        HisHtmlSafePipe,
        HisReplacePipe,
        HisUiPipe,
        HisUiddPipe,
        HisDatePipe,
        HisLowercasefirstletterPipe,
        HisUppercasefirstletterPipe,

    ],

    exports: [
        // Components
        HisPageComponent,
        NotificationsComponent,
        HisConfirmDialogComponent,
        HisBrowserBackButtonLockComponent,
        HisPlamComponent,
        HisSafeHtmlComponent,
        HisWaitingAnimationComponent,
        HisRowsPerPageComponent,

        // Directives
        HisImageDirective,
        HisDdDirective,
        HisPlamDirective,
        HisDdValidator,
        HisAutoCompleteEagerDataSourceDirective,
        HisAutoCompleteLazyDataSourceDirective,
        HisCalendarDefaultsDirective,
        HisChipsDirective,
        HisFormDefaultsDirective,
        HisPageTitleDirective,
        HisModificationDetectionDirective,
        HisMultiselectDefaultsDirective,
        HisRestoreFocusDirective,
        HisScrollwithmeDirective,
        HisFrozenColDirective,
        HisButtonTitleDirective,
        HisDeleteActionDirective,
        HisDialogDirective,
        HisGlobalRestoreFocusDirective,
        HisStepsDirective,
        HisScrollNearEndDirective,
        HisTrimInputDirective,
        HisFileUploadDirective,
        HisPanelDirective,
        HisPanelHelptextDirective,
        HisTableDirective,
        HisSearchResultHeaderContextProviderDirective,
        HisSummernoteComponent,
        HisIntegerValidatorDirective,
        HisNumberValidatorDirective,
        HisListboxDirective,
        HisViewContextDirective,
        HisDdContextsDirective,

        // Pipes
        HisAmountPipe,
        HisHtmlUnsafePipe,
        HisHtmlSafePipe,
        HisReplacePipe,
        HisUiPipe,
        HisUiddPipe,
        HisDatePipe,
        HisLowercasefirstletterPipe,
        HisUppercasefirstletterPipe,

    ]
    // NO PROVIDERS! Providers need to go into HisCoreCoreModule
} )
export class HisCoreSharedModule { }
